import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChatBubbleOvalLeftIcon,
  MagnifyingGlassIcon,
  FireIcon,
  ClockIcon,
  ChevronRightIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ForumsList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [loading, setLoading] = useState(true);
  const [forums, setForums] = useState([]);
  const [recentDiscussions, setRecentDiscussions] = useState([]);

  useEffect(() => {
    // Sample forum categories data
    const forumData = [
      {
        id: 1,
        name: "Medical Research & Innovation",
        description: "Latest developments in medical research, new treatments, and healthcare innovations",
        category: "medical",
        threads: 124,
        posts: 1840,
        lastActivity: "2 minutes ago",
        lastPost: {
          title: "New AI-Powered Diagnostic Tools: Game Changer for Radiology",
          author: "Dr. Rajesh Kumar",
          timestamp: "2 minutes ago"
        },
        moderators: ["Dr. Sarah Johnson", "Dr. Michael Chen"],
        image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop",
        color: "from-blue-500 to-indigo-600"
      },
      {
        id: 2,
        name: "Staff Wellness & Mental Health",
        description: "Support programs, wellness initiatives, and mental health resources for healthcare professionals",
        category: "wellness",
        threads: 89,
        posts: 1245,
        lastActivity: "15 minutes ago",
        lastPost: {
          title: "Mental Health Support Programs: Staff Wellness Initiative",
          author: "Kavya Patel",
          timestamp: "15 minutes ago"
        },
        moderators: ["Dr. Lisa Rodriguez", "Sarah Wilson"],
        image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=250&fit=crop",
        color: "from-emerald-500 to-teal-600"
      },
      {
        id: 3,
        name: "Surgical Procedures & Techniques",
        description: "Discussions on surgical innovations, techniques, and best practices",
        category: "medical",
        threads: 156,
        posts: 2100,
        lastActivity: "1 hour ago",
        lastPost: {
          title: "Breakthrough in Cardiac Surgery: Minimally Invasive Procedures",
          author: "Dr. Sushma Iyer",
          timestamp: "1 hour ago"
        },
        moderators: ["Dr. Robert Kim", "Dr. Amanda Foster"],
        image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=250&fit=crop",
        color: "from-purple-500 to-indigo-600"
      },
      {
        id: 4,
        name: "Healthcare Technology & Digital Innovation",
        description: "IT solutions, digital health records, and technological advances in healthcare",
        category: "technology",
        threads: 78,
        posts: 890,
        lastActivity: "3 hours ago",
        lastPost: {
          title: "Digital Health Records: Streamlining Healthcare Worldwide",
          author: "Vikram Malhotra",
          timestamp: "3 hours ago"
        },
        moderators: ["Tech Team Lead", "Dr. James Park"],
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=250&fit=crop",
        color: "from-cyan-500 to-blue-600"
      },
      {
        id: 5,
        name: "Nursing Excellence & Patient Care",
        description: "Best practices in nursing, patient care strategies, and professional development",
        category: "nursing",
        threads: 203,
        posts: 2890,
        lastActivity: "5 hours ago",
        lastPost: {
          title: "Nursing Excellence Awards: Celebrating Our Healthcare Heroes",
          author: "Ritu Agarwal",
          timestamp: "5 hours ago"
        },
        moderators: ["Head Nurse Maria", "Supervisor Johnson"],
        image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=250&fit=crop",
        color: "from-rose-500 to-pink-600"
      },
      {
        id: 6,
        name: "General Discussion & Community",
        description: "General conversations, introductions, and community building",
        category: "general",
        threads: 345,
        posts: 4200,
        lastActivity: "10 minutes ago",
        lastPost: {
          title: "Welcome New Team Members - July 2025",
          author: "HR Team",
          timestamp: "10 minutes ago"
        },
        moderators: ["Community Manager", "Dr. Alex Thompson"],
        image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop",
        color: "from-amber-500 to-orange-600"
      }
    ];

    // Sample recent discussions data (extracted from Homepage.js)
    const discussionsData = [
      {
        id: 1,
        title: "New AI-Powered Diagnostic Tools: Game Changer for Radiology",
        image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop",
        author: "Dr. Rajesh Kumar",
        department: "Radiology",
        likes: 42,
        comments: 18,
        views: 234,
        timeAgo: "2 hours ago",
        forumId: 1,
        isPinned: true,
        isHot: true
      },
      {
        id: 2,
        title: "Mental Health Support Programs: Staff Wellness Initiative",
        image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=250&fit=crop",
        author: "Kavya Patel",
        department: "HR Wellness",
        likes: 38,
        comments: 24,
        views: 189,
        timeAgo: "5 hours ago",
        forumId: 2,
        isPinned: false,
        isHot: true
      },
      {
        id: 3,
        title: "Breakthrough in Cardiac Surgery: Minimally Invasive Procedures",
        image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=250&fit=crop",
        author: "Dr. Sushma Iyer",
        department: "Cardiac Surgery",
        likes: 67,
        comments: 31,
        views: 456,
        timeAgo: "1 day ago",
        forumId: 3,
        isPinned: false,
        isHot: true
      },
      {
        id: 4,
        title: "Digital Health Records: Streamlining Healthcare Worldwide",
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=250&fit=crop",
        author: "Vikram Malhotra",
        department: "IT Innovation",
        likes: 29,
        comments: 12,
        views: 167,
        timeAgo: "2 days ago",
        forumId: 4,
        isPinned: false,
        isHot: false
      },
      {
        id: 5,
        title: "Nursing Excellence Awards: Celebrating Our Healthcare Heroes",
        image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=250&fit=crop",
        author: "Ritu Agarwal",
        department: "Nursing Administration",
        likes: 84,
        comments: 45,
        views: 678,
        timeAgo: "3 days ago",
        forumId: 5,
        isPinned: true,
        isHot: false
      }
    ];

    // Simulate loading
    setTimeout(() => {
      setForums(forumData);
      setRecentDiscussions(discussionsData);
      setLoading(false);
    }, 1000);
  }, []);

  const categories = [
    { id: 'all', name: 'All Categories', icon: '🌟' },
    { id: 'medical', name: 'Medical Research', icon: '🏥' },
    { id: 'wellness', name: 'Staff Wellness', icon: '💚' },
    { id: 'technology', name: 'Healthcare Tech', icon: '💻' },
    { id: 'nursing', name: 'Nursing Care', icon: '👩‍⚕️' },
    { id: 'general', name: 'General Discussion', icon: '💬' }
  ];

  const filteredForums = forums.filter(forum => {
    const matchesSearch = forum.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         forum.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || forum.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedForums = [...filteredForums].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.lastActivity) - new Date(a.lastActivity);
      case 'popular':
        return b.posts - a.posts;
      case 'threads':
        return b.threads - a.threads;
      default:
        return 0;
    }
  });

  const handleForumClick = (forumId) => {
    navigate(`/forums/${forumId}`);
  };

  const handleThreadClick = (threadId) => {
    navigate(`/forums/thread/${threadId}`);
  };

  // eslint-disable-next-line no-unused-vars
  const toggleLike = (discussionId) => {
    setRecentDiscussions(prev => 
      prev.map(discussion => 
        discussion.id === discussionId 
          ? { ...discussion, likes: discussion.likes + 1 }
          : discussion
      )
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading community forums...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent text-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ChatBubbleOvalLeftIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-80" />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">Community Forums</h1>
            <p className="text-base sm:text-lg lg:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Connect, share knowledge, and engage in meaningful discussions with healthcare professionals
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-lg">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search forums and discussions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 text-sm sm:text-base"
              />
            </div>

            {/* Category and Sort Filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {/* Category Filter */}
              <div className="relative">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full sm:w-auto pl-8 sm:pl-10 pr-8 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white text-sm sm:text-base"
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white text-sm sm:text-base"
              >
                <option value="recent">Most Recent</option>
                <option value="popular">Most Popular</option>
                <option value="threads">Most Threads</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Forums List */}
          <div className="lg:col-span-2">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Forum Categories</h2>
              <p className="text-gray-600 text-sm sm:text-base">Browse discussions by topic and specialty</p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {sortedForums.map(forum => (
                <div
                  key={forum.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-apollo-accent/30 cursor-pointer"
                  onClick={() => handleForumClick(forum.id)}
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Forum Image */}
                    <div className="relative w-full sm:w-48 h-32 sm:h-32 flex-shrink-0">
                      <img
                        src={forum.image}
                        alt={forum.name}
                        className="w-full h-full object-cover"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-r ${forum.color} opacity-80`}></div>
                    </div>

                    {/* Forum Info */}
                    <div className="flex-1 p-4 sm:p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-bold text-apollo-primary mb-2 hover:text-apollo-accent transition-colors line-clamp-2">
                            {forum.name}
                          </h3>
                          <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                            {forum.description}
                          </p>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2 sm:ml-4" />
                      </div>

                      {/* Forum Stats */}
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6 mb-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1" />
                          <span>{forum.threads} threads</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <ChatBubbleOvalLeftIcon className="w-4 h-4 mr-1" />
                          <span>{forum.posts.toLocaleString()} posts</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          <span className="truncate">{forum.lastActivity}</span>
                        </div>
                      </div>

                      {/* Last Post Info */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Latest Discussion:</div>
                        <div className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                          {forum.lastPost.title}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          by {forum.lastPost.author} • {forum.lastPost.timestamp}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 sm:space-y-8">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/forums/popular')}
                  className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-2 sm:py-3 px-4 rounded-xl hover:from-apollo-accent/90 hover:to-blue-600/90 transition-colors font-medium flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <FireIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Popular Discussions</span>
                </button>
                <button className="w-full border border-apollo-accent text-apollo-accent py-2 sm:py-3 px-4 rounded-xl hover:bg-apollo-accent/5 transition-colors font-medium text-sm sm:text-base">
                  Start New Discussion
                </button>
              </div>
            </div>

            {/* Recent Discussions */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Recent Discussions</h3>
              <div className="space-y-4">
                {recentDiscussions.slice(0, 5).map(discussion => (
                  <div
                    key={discussion.id}
                    className="border-b border-gray-100 pb-4 last:border-b-0 cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors"
                    onClick={() => handleThreadClick(discussion.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <img
                        src={discussion.image}
                        alt={discussion.title}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          {discussion.isPinned && (
                            <span className="text-xs bg-apollo-accent text-white px-2 py-0.5 rounded-full">📌 Pinned</span>
                          )}
                          {discussion.isHot && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center">
                              <FireIcon className="w-3 h-3 mr-1" />
                              Hot
                            </span>
                          )}
                        </div>
                        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                          {discussion.title}
                        </h4>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                          <span className="truncate">{discussion.author}</span>
                          <span className="whitespace-nowrap">{discussion.timeAgo}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <HeartIcon className="w-3 h-3" />
                            <span className="text-xs">{discussion.likes}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ChatBubbleOvalLeftIcon className="w-3 h-3" />
                            <span className="text-xs">{discussion.comments}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <EyeIcon className="w-3 h-3" />
                            <span className="text-xs">{discussion.views}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Forum Stats */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Community Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Forums</span>
                  <span className="font-semibold text-apollo-primary">{forums.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Discussions</span>
                  <span className="font-semibold text-apollo-primary">
                    {forums.reduce((acc, forum) => acc + forum.threads, 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Posts</span>
                  <span className="font-semibold text-apollo-primary">
                    {forums.reduce((acc, forum) => acc + forum.posts, 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Active Members</span>
                  <span className="font-semibold text-apollo-primary">1,247</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForumsList;
